import type { Tone } from "./orders";
import type { QbEntry } from "./types";

// What the QuickBooks screen works out from qb_entries (phase 5): an entry's kind and state in words, the piles it falls
// into, and a line's unit figure (a bill's cost, an invoice's price). Pure, so it is tested without a browser.

const KIND: Record<string, string> = {
  BILL: "Bill from the supplier", INVOICE: "Invoice to the customer", COMMISSION_INVOICE: "Commission invoice to the supplier",
  CREDIT_MEMO: "Credit memo to the customer", VENDOR_CREDIT: "Credit from the supplier",
};

export function kindWords(kind: string): string {
  return KIND[kind] ?? kind.toLowerCase().replace(/_/g, " ");
}

const STATUS: Record<string, { text: string; tone: Tone }> = {
  WAITING: { text: "Waiting", tone: "neutral" },
  PROPOSED: { text: "To approve", tone: "soon" },
  APPROVED: { text: "Approved, posting", tone: "good" },
  POSTED: { text: "In QuickBooks", tone: "good" },
  REJECTED: { text: "Rejected", tone: "neutral" },
  FAILED: { text: "Didn't post", tone: "late" },
};

export function entryState(status: string): { text: string; tone: Tone } {
  return STATUS[status] ?? { text: status.toLowerCase().replace(/_/g, " "), tone: "neutral" };
}

export interface Piles { toApprove: QbEntry[]; failed: QbEntry[]; waiting: QbEntry[]; posting: QbEntry[]; done: QbEntry[] }

/** The screen's piles, each oldest first except what's done (newest first). */
export function piles(rows: QbEntry[]): Piles {
  const by = (s: string) => rows.filter((r) => r.status === s).sort((a, b) => a.qbEntryId - b.qbEntryId);
  return {
    toApprove: by("PROPOSED"), failed: by("FAILED"), waiting: by("WAITING"), posting: by("APPROVED"),
    done: rows.filter((r) => r.status === "POSTED" || r.status === "REJECTED").sort((a, b) => b.qbEntryId - a.qbEntryId),
  };
}

/** A line's unit figure: the supplier's cost on a bill or a credit from it, Tare's price otherwise. */
export function unitFigure(kind: string, l: QbEntry["lines"][number]): number | undefined {
  return kind === "BILL" || kind === "VENDOR_CREDIT" ? l.unitCost ?? l.unitPrice : l.unitPrice ?? l.unitCost;
}

/** The heading of a line's unit figure, by kind. */
export function unitHeading(kind: string): string {
  if (kind === "BILL" || kind === "VENDOR_CREDIT") return "Cost / EA";
  return kind === "COMMISSION_INVOICE" ? "Commission / EA" : "Price / EA";
}

/** A per-EA figure to as many decimals as it has (at least 3, up to 6), so a line's arithmetic can be checked. */
export function exactEa(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return "";
  return n.toLocaleString("en-US", { minimumFractionDigits: 3, maximumFractionDigits: 6 });
}

export const CREDIT_REASONS = { SHORT: "Short", DAMAGED: "Damaged", REFUSED: "Refused" } as const;
export const CREDIT_FAULTS = { SUPPLIER: "The supplier", CARRIER: "The carrier", TARE: "Tare", CUSTOMER: "The customer" } as const;

/** Can a credit be recorded on this entry: a customer invoice that's in QuickBooks. */
export function creditable(e: Pick<QbEntry, "kind" | "status" | "customerInvoiceId">): boolean {
  return e.kind === "INVOICE" && e.status === "POSTED" && !!e.customerInvoiceId;
}

/** Is this the supplier's credit waiting for their credit memo (record it when it arrives). */
export function awaitingSupplierMemo(e: Pick<QbEntry, "kind" | "status" | "creditMemoId">): boolean {
  return e.kind === "VENDOR_CREDIT" && e.status === "WAITING" && !!e.creditMemoId;
}

/** The arguments of record_credit to start a credit on an invoice, or what's missing. */
export function creditArgs(e: QbEntry, f: { sku: string; qty: string; reason: string; fault: string; note: string }):
  { args: Record<string, unknown> } | { missing: string } {
  const line = e.lines.find((l) => l.sku === f.sku);
  if (!line) return { missing: "Choose the item." };
  const t = f.qty.replace(/,/g, "").trim();
  if (!/^\d+$/.test(t) || Number(t) < 1) return { missing: "Give the quantity to credit, a whole number of EA." };
  const n = Number(t);
  if (n > line.qtyEa) return { missing: `That's more than the ${line.qtyEa.toLocaleString("en-US")} EA invoiced.` };
  if (!(f.reason in CREDIT_REASONS)) return { missing: "Say what happened: short, damaged or refused." };
  if (!(f.fault in CREDIT_FAULTS)) return { missing: "Say whose fault it is." };
  if (!f.note.trim()) return { missing: "Say what was confirmed, and with whom." };
  return { args: { customer_invoice_id: e.customerInvoiceId, sku: f.sku, qty_ea: n, reason: f.reason, fault: f.fault, note: f.note.trim() } };
}

/** The arguments of record_credit when the supplier's credit memo arrives, or what's missing. */
export function supplierMemoArgs(creditMemoId: number, ref: string, note: string): { args: Record<string, unknown> } | { missing: string } {
  if (!ref.trim()) return { missing: "Give the supplier's credit memo number, as written." };
  return { args: { credit_memo_id: creditMemoId, supplier_credit_ref: ref.trim(), ...(note.trim() ? { note: note.trim() } : {}) } };
}

/** What recording a credit did, in the app's words (the tool's note is written for Claude). */
export function creditDone(entries: { kind: string; status: string }[] | undefined, supplierMemo = false): string {
  if (supplierMemo) return "Recorded. The supplier's credit is now to approve; the customer's credit follows once it's in QuickBooks.";
  const waiting = (entries ?? []).some((x) => x.kind === "VENDOR_CREDIT");
  return waiting
    ? "Recorded. The supplier is at fault, so the customer's credit waits for the supplier's: when their credit memo arrives, record its number on the waiting supplier credit below."
    : "Recorded. The customer's credit memo is now to approve.";
}

/** The arguments of approve_qb_entry, or what's missing: a rejection says why. */
export function decisionArgs(id: number, decision: "APPROVE" | "REJECT", note: string): { args: Record<string, unknown> } | { missing: string } {
  const n = note.trim();
  if (decision === "REJECT" && !n) return { missing: "Say why it's rejected, then reject it." };
  return { args: { qb_entry_id: id, decision, ...(n ? { note: n } : {}) } };
}
